import { Tag } from 'lucide-react'
import { ClassifiedsSpaceFields } from '@/components/features/classifieds/ClassifiedsSpaceFields'
import {
  apiFieldErrors,
  defaultClassifiedsConfig,
  toSpaceInput,
  validateClassifiedsConfig
} from '@/components/features/classifieds/classifiedUtils'
import { sdk } from '@/lib/sdk'
import type { ModuleDefinition } from '../types'
import { classifiedRow } from '../layouts/rows'
import { lazyPage } from '@/lib/lazyPage'

const ClassifiedsPage = lazyPage(() => import('@/pages/Classifieds'), 'ClassifiedsPage')
const ClassifiedDetailsPage = lazyPage(() => import('@/pages/ClassifiedDetails'), 'ClassifiedDetailsPage')

export const classifiedsModule: ModuleDefinition = {
  key: 'classifieds',
  icon: Tag,
  iconName: 'tag',
  namespaces: ['classifieds'],
  ready: true,
  /* F-198: the default is what the page always drew. */
  layouts: { fallback: 'cards', renders: ['cards', 'list'], toRow: classifiedRow },
  ListPage: ClassifiedsPage,
  renderDetail: ({ spaceData }) => <ClassifiedDetailsPage spaceId={spaceData.id} spaceData={spaceData} />,
  /* The terms (categories, durations, value, approval) have a body of their own: a third step (PADROES-UI section 14). */
  setup: {
    wizardStep: 'own',
    stepLabelKey: 'common:spaces.stepClassifieds',
    stepHintKey: 'classifieds:config.stepHint',
    defaultConfig: (t) => defaultClassifiedsConfig(t),
    validate: (config, t) => validateClassifiedsConfig(config, t),
    Fields: ClassifiedsSpaceFields,
    persist: {
      type: 'endpoint',
      /* The terms live in `ClassifiedSpace`; the space is born with the default ones. */
      save: (spaceId, config) => sdk.classifieds.updateSpace(spaceId, toSpaceInput(config)),
      load: async (spaceId) => {
        const config = await sdk.classifieds.getSpace(spaceId)
        /* The controls are for owner and admin only; the server says who that is. */
        if (!config.canManage) return null
        return {
          categories: config.categories.map((c) => ({ id: c.id, name: c.name, adsCount: c.adsCount })),
          allowedDurations: config.allowedDurations,
          showValue: config.showValue,
          requireApproval: config.requireApproval
        }
      },
      /* The space is saved; the terms were refused (a category with ads, say). */
      errorsFromSaveFailure: (err) => {
        const fields = apiFieldErrors(err)
        return { categories: fields.categories, durations: fields.allowedDurations }
      }
    }
  }
}
